import { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Badge } from '@/components/ui/badge'
import { 
  ArrowLeft, 
  Upload, 
  FileText, 
  CheckCircle, 
  XCircle, 
  Clock, 
  Download,
  Search,
  Filter
} from 'lucide-react'
import { formatCurrency } from '@/lib/utils'

interface TransacaoBancaria {
  id: string
  data: string
  descricao: string
  valor: number
  tipo: 'entrada' | 'saida'
  status: 'conciliado' | 'pendente' | 'divergente'
  entradaId?: string
}

export function ConciliacaoBancariaPage() {
  const navigate = useNavigate()
  const [arquivo, setArquivo] = useState<File | null>(null)
  const [transacoesBancarias, setTransacoesBancarias] = useState<TransacaoBancaria[]>([])
  const [filtroStatus, setFiltroStatus] = useState<string>('todos')
  const [termoBusca, setTermoBusca] = useState('')
  const [loading, setLoading] = useState(false)

  // Mock data para demonstração
  const mockTransacoes: TransacaoBancaria[] = [
    {
      id: '1',
      data: '2024-01-15',
      descricao: 'PIX - João Silva',
      valor: 500.00,
      tipo: 'entrada',
      status: 'conciliado',
      entradaId: 'E001'
    },
    {
      id: '2',
      data: '2024-01-16',
      descricao: 'TED - Maria Santos',
      valor: 1200.00,
      tipo: 'entrada',
      status: 'pendente'
    },
    {
      id: '3',
      data: '2024-01-17',
      descricao: 'PIX - Pedro Oliveira',
      valor: 300.00,
      tipo: 'entrada',
      status: 'divergente'
    },
    {
      id: '4',
      data: '2024-01-18',
      descricao: 'DOC - Ana Costa',
      valor: 800.00,
      tipo: 'entrada',
      status: 'conciliado',
      entradaId: 'E002'
    }
  ]

  const handleImportarExtrato = () => {
    if (!arquivo) {
      alert('Por favor, selecione um arquivo para importar.')
      return
    }

    setLoading(true)
    
    // Simular importação do arquivo
    setTimeout(() => {
      setTransacoesBancarias(mockTransacoes)
      setLoading(false)
      alert(`Extrato importado com sucesso! ${mockTransacoes.length} transações encontradas.`)
    }, 2000)
  }

  const handleConciliarTransacao = (transacaoId: string) => {
    setTransacoesBancarias(prev => 
      prev.map(t => 
        t.id === transacaoId 
          ? { ...t, status: 'conciliado' as const, entradaId: `E${Date.now()}` }
          : t
      )
    )
    alert('Transação conciliada com sucesso!')
  }

  const handleMarcarDivergente = (transacaoId: string) => {
    setTransacoesBancarias(prev => 
      prev.map(t => 
        t.id === transacaoId 
          ? { ...t, status: 'divergente' as const }
          : t
      )
    )
  }

  const handleStatusClick = (transacaoId: string) => {
    setTransacoesBancarias(prev => 
      prev.map(transacao => {
        if (transacao.id === transacaoId) {
          const statusOrder: ('conciliado' | 'pendente' | 'divergente')[] = ['conciliado', 'pendente', 'divergente'];
          const currentIndex = statusOrder.indexOf(transacao.status);
          const nextIndex = (currentIndex + 1) % statusOrder.length;
          return { ...transacao, status: statusOrder[nextIndex] };
        }
        return transacao;
      })
    )
  }

  const transacoesFiltradas = transacoesBancarias.filter(transacao => {
    const matchStatus = filtroStatus === 'todos' || transacao.status === filtroStatus
    const matchBusca = transacao.descricao.toLowerCase().includes(termoBusca.toLowerCase())
    return matchStatus && matchBusca
  })

  const estatisticas = {
    total: transacoesBancarias.length,
    conciliadas: transacoesBancarias.filter(t => t.status === 'conciliado').length,
    pendentes: transacoesBancarias.filter(t => t.status === 'pendente').length,
    divergentes: transacoesBancarias.filter(t => t.status === 'divergente').length,
    valorTotal: transacoesBancarias.reduce((acc, t) => acc + t.valor, 0)
  }

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'conciliado':
        return <CheckCircle className="h-4 w-4 text-green-600" />
      case 'pendente':
        return <Clock className="h-4 w-4 text-yellow-600" />
      case 'divergente':
        return <XCircle className="h-4 w-4 text-red-600" />
      default:
        return null
    }
  }

  const getStatusBadge = (status: string, transacaoId: string) => {
    const variants = {
      conciliado: 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200',
      pendente: 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-200',
      divergente: 'bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200'
    }
    
    const labels = {
      conciliado: 'Conciliado',
      pendente: 'Pendente',
      divergente: 'Divergente'
    }

    return (
      <Badge 
        className={`${variants[status as keyof typeof variants]} cursor-pointer relative group`}
        onClick={() => handleStatusClick(transacaoId)}
        title="Clique para mudar status"
      >
        {labels[status as keyof typeof labels]}
        <span className="absolute bottom-full left-1/2 transform -translate-x-1/2 mb-2 px-2 py-1 text-xs text-white bg-gray-900 rounded opacity-0 group-hover:opacity-100 transition-opacity duration-200 pointer-events-none whitespace-nowrap z-10">
          Clique para mudar status
        </span>
      </Badge>
    )
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4">
          <Button 
            variant="ghost" 
            onClick={() => navigate('/financas/administracao/entradas')}
          >
            <ArrowLeft className="h-4 w-4 mr-2" />
            Voltar
          </Button>
          <div>
            <h1 className="text-2xl font-bold">Conciliação Bancária</h1>
            <p className="text-muted-foreground">Importe e concilie extratos bancários com as entradas registradas</p>
          </div>
        </div>
      </div>

      {/* Importação de Extrato */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Upload className="h-5 w-5" />
            Importar Extrato Bancário
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div>
            <Label htmlFor="arquivo">Selecionar Arquivo</Label>
            <div className="flex items-center gap-4 mt-2">
              <Input
                id="arquivo"
                type="file"
                accept=".csv,.xls,.xlsx"
                onChange={(e) => setArquivo(e.target.files?.[0] || null)}
                className="flex-1"
              />
              <Button 
                onClick={handleImportarExtrato}
                disabled={!arquivo || loading}
                className="bg-blue-600 hover:bg-blue-700"
              >
                {loading ? (
                  <>
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                    Importando...
                  </>
                ) : (
                  <>
                    <Upload className="h-4 w-4 mr-2" />
                    Importar
                  </>
                )}
              </Button>
            </div>
            <p className="text-xs text-muted-foreground mt-1">
              Formatos aceitos: CSV, XLS, XLSX
            </p>
          </div>

          {arquivo && (
            <div className="flex items-center gap-2 p-3 bg-blue-50 rounded-lg">
              <FileText className="h-4 w-4 text-blue-600" />
              <span className="text-sm font-medium">{arquivo.name}</span>
              <span className="text-xs text-muted-foreground">
                ({(arquivo.size / 1024).toFixed(1)} KB)
              </span>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Estatísticas */}
      {transacoesBancarias.length > 0 && (
        <div className="grid grid-cols-1 md:grid-cols-5 gap-4">
          <Card>
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-muted-foreground">Total</p>
                  <p className="text-2xl font-bold">{estatisticas.total}</p>
                </div>
                <FileText className="h-8 w-8 text-blue-600" />
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-muted-foreground">Conciliadas</p>
                  <p className="text-2xl font-bold text-green-600">{estatisticas.conciliadas}</p>
                </div>
                <CheckCircle className="h-8 w-8 text-green-600" />
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-muted-foreground">Pendentes</p>
                  <p className="text-2xl font-bold text-yellow-600">{estatisticas.pendentes}</p>
                </div>
                <Clock className="h-8 w-8 text-yellow-600" />
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-muted-foreground">Divergentes</p>
                  <p className="text-2xl font-bold text-red-600">{estatisticas.divergentes}</p>
                </div>
                <XCircle className="h-8 w-8 text-red-600" />
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-muted-foreground">Valor Total</p>
                  <p className="text-lg font-bold text-blue-600">{formatCurrency(estatisticas.valorTotal)}</p>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Filtros e Busca */}
      {transacoesBancarias.length > 0 && (
        <Card>
          <CardContent className="p-4">
            <div className="flex flex-col md:flex-row gap-4">
              <div className="flex-1">
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
                  <Input
                    placeholder="Buscar por descrição..."
                    value={termoBusca}
                    onChange={(e) => setTermoBusca(e.target.value)}
                    className="pl-10"
                  />
                </div>
              </div>
              
              <div className="flex items-center gap-2">
                <Filter className="h-4 w-4 text-muted-foreground" />
                <select
                  value={filtroStatus}
                  onChange={(e) => setFiltroStatus(e.target.value)}
                  className="px-3 py-2 border border-gray-300 rounded-md text-sm"
                >
                  <option value="todos">Todos os Status</option>
                  <option value="conciliado">Conciliadas</option>
                  <option value="pendente">Pendentes</option>
                  <option value="divergente">Divergentes</option>
                </select>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Lista de Transações */}
      {transacoesBancarias.length > 0 && (
        <Card>
          <CardHeader>
            <CardTitle>Transações Bancárias</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-3">
              {transacoesFiltradas.map((transacao) => (
                <div 
                  key={transacao.id} 
                  className="flex items-center justify-between p-4 border rounded-lg hover:bg-muted"
                >
                  <div className="flex items-center gap-4">
                    {getStatusIcon(transacao.status)}
                    <div>
                      <p className="font-medium">{transacao.descricao}</p>
                      <p className="text-sm text-muted-foreground">
                        {new Date(transacao.data).toLocaleDateString('pt-BR')}
                        {transacao.entradaId && (
                          <span className="ml-2 text-blue-600">• ID: {transacao.entradaId}</span>
                        )}
                      </p>
                    </div>
                  </div>

                  <div className="flex items-center gap-4">
                    <div className="text-right">
                      <p className="font-semibold text-green-600">
                        {formatCurrency(transacao.valor)}
                      </p>
                      {getStatusBadge(transacao.status, transacao.id)}
                    </div>

                    <div className="flex gap-2">
                      {transacao.status === 'pendente' && (
                        <>
                          <Button
                            size="sm"
                            onClick={() => handleConciliarTransacao(transacao.id)}
                            className="bg-green-600 hover:bg-green-700"
                          >
                            <CheckCircle className="h-4 w-4 mr-1" />
                            Conciliar
                          </Button>
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => handleMarcarDivergente(transacao.id)}
                            className="border-red-300 text-red-600 hover:bg-red-50"
                          >
                            <XCircle className="h-4 w-4 mr-1" />
                            Divergente
                          </Button>
                        </>
                      )}
                    </div>
                  </div>
                </div>
              ))}

              {transacoesFiltradas.length === 0 && transacoesBancarias.length > 0 && (
                <div className="text-center py-8">
                  <Search className="mx-auto h-12 w-12 text-muted-foreground" />
                  <h3 className="mt-2 text-sm font-semibold">Nenhuma transação encontrada</h3>
                  <p className="mt-1 text-sm text-muted-foreground">
                    Tente ajustar os filtros ou termos de busca.
                  </p>
                </div>
              )}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Estado vazio */}
      {transacoesBancarias.length === 0 && (
        <Card>
          <CardContent className="py-12">
            <div className="text-center">
              <Upload className="mx-auto h-12 w-12 text-muted-foreground" />
              <h3 className="mt-2 text-sm font-semibold">Nenhum extrato importado</h3>
              <p className="mt-1 text-sm text-muted-foreground">
                Importe um extrato bancário para começar a conciliação.
              </p>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Ações em lote */}
      {transacoesBancarias.length > 0 && (
        <Card>
          <CardContent className="p-4">
            <div className="flex justify-between items-center">
              <div className="text-sm text-muted-foreground">
                {transacoesFiltradas.length} de {transacoesBancarias.length} transações exibidas
              </div>
              
              <div className="flex gap-2">
                <Button variant="outline" size="sm">
                  <Download className="h-4 w-4 mr-2" />
                  Exportar Relatório
                </Button>
                
                <Button 
                  size="sm"
                  onClick={() => {
                    const pendentes = transacoesBancarias.filter(t => t.status === 'pendente')
                    if (pendentes.length > 0) {
                      setTransacoesBancarias(prev => 
                        prev.map(t => 
                          t.status === 'pendente' 
                            ? { ...t, status: 'conciliado' as const, entradaId: `E${Date.now()}-${t.id}` }
                            : t
                        )
                      )
                      alert(`${pendentes.length} transações conciliadas automaticamente!`)
                    }
                  }}
                  className="bg-green-600 hover:bg-green-700"
                >
                  <CheckCircle className="h-4 w-4 mr-2" />
                  Conciliar Todas Pendentes
                </Button>
              </div>
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  )
}

export default ConciliacaoBancariaPage